import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Fragment, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Scale, Wallet, CalendarDays, UserX, ShieldAlert, ImageIcon, ExternalLink } from 'lucide-react'
import { idTypeLabel, useAdminChecks, useCheckPhotos, useDecideCheck, type AdminCheck } from '@/lib/verification'
import { supabase } from '@/lib/supabase'
import { isMarkedPlatformAdmin, isPlatformAdmin } from '@/lib/admin'
import { useAuth } from '@/hooks/use-auth'
import { BrandMark } from '@/components/brand'
import { btnPrimary, btnSecondary, EmptyState, SkeletonRows, TabPills, useConfirm } from '@/components/rm'
import { Field, Input, Select, Textarea } from '@/components/rm/form'
import { fmtDate, fmtDateTime, manilaDayIso, pesoFromCentavos } from '@/lib/ph'
import { cn, errorText } from '@/lib/utils'
import {
  REASON_EN, useAdminDisputes, useDisputeDetail, useFindAccounts, useHolidays, useOpsActions, usePayoutQueue,
  type AccountRow, type AdminDisputeRow, type DisputeDetail,
} from '@/lib/disputes'

// Spec 009. The MySerbisyo operations console: disputes, payouts sent by hand, holidays,
// suspensions. Platform admins only, with the second factor in this session (FR-005). The gate
// here is for the person; the database and the edge function refuse everyone else anyway.
// English only (FR-010).
export const Route = createFileRoute('/ops')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) throw redirect({ to: '/login', search: { next: '/ops' } })
    if (await isPlatformAdmin()) return
    if (!(await isMarkedPlatformAdmin())) throw redirect({ to: '/' })
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') throw redirect({ to: '/mfa-challenge', search: { next: '/ops' } })
    // Marked, but no authenticator yet: the component says what to do.
  },
  component: Ops,
})

type Tab = 'disputes' | 'verification' | 'payouts' | 'holidays' | 'accounts'

function Ops() {
  const [tab, setTab] = useState<Tab>('disputes')
  const gate = useQuery({ queryKey: ['ops', 'gate'], queryFn: isPlatformAdmin })
  const ready = gate.data ?? null
  const { signOut } = useAuth()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-canvas-subtle">
      <header className="border-b border-token-subtle bg-token-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <BrandMark className="h-8 w-8" />
            <div>
              <p className="font-display text-[15px] font-bold leading-tight text-token-primary">MySerbisyo operations</p>
              <p className="hidden text-[12px] text-token-secondary sm:block">Every action here is logged with your name.</p>
            </div>
          </div>
          <button type="button" className={cn(btnSecondary, 'whitespace-nowrap')} onClick={async () => { await signOut(); navigate({ to: '/login' }) }}>Sign out</button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-4 px-4 py-5">
        {ready === false ? <NeedsFactor /> : ready === null ? <SkeletonRows /> : (
          <>
            <div className="-mx-4 overflow-x-auto px-4"><TabPills active={tab} onChange={(id) => setTab(id as Tab)} chips={[
              { id: 'disputes', label: 'Disputes' }, { id: 'verification', label: 'Verification' }, { id: 'payouts', label: 'Payouts' }, { id: 'holidays', label: 'Holidays' }, { id: 'accounts', label: 'Accounts' },
            ]} /></div>
            {tab === 'disputes' && <Disputes />}
            {tab === 'verification' && <Verifications />}
            {tab === 'payouts' && <Payouts />}
            {tab === 'holidays' && <Holidays />}
            {tab === 'accounts' && <Accounts />}
          </>
        )}
      </main>
    </div>
  )
}

function NeedsFactor() {
  return (
    <section className="mx-auto max-w-md space-y-2 rounded-[16px] border border-token-warning bg-token-warning-surface p-5">
      <p className="flex items-center gap-2 text-[15px] font-bold text-token-primary"><ShieldAlert className="h-5 w-5 text-token-warning" />Set up an authenticator app first</p>
      <p className="text-[13.5px] text-token-secondary">This account is a platform admin, but the console also needs the second factor in each session. Add an authenticator app in Account, Security, then sign in again.</p>
    </section>
  )
}

const panel = 'rounded-[16px] border border-token-subtle bg-token-surface'
const h2 = 'text-[12px] font-bold uppercase tracking-[0.06em] text-token-muted'

function daysLeft(due: string): { text: string; late: boolean } {
  // Calendar days in Manila, so a dispute due on Monday reads "5 days left" all of Wednesday.
  const d = Math.round((Date.parse(manilaDayIso(due)) - Date.parse(manilaDayIso())) / 86_400_000)
  if (new Date(due).getTime() < Date.now()) return { text: d < 0 ? `${-d} day${d === -1 ? '' : 's'} late` : 'late', late: true }
  return { text: d === 0 ? 'due today' : d === 1 ? '1 day left' : `${d} days left`, late: false }
}

// --- Disputes --------------------------------------------------------------------------------

function Disputes() {
  const q = useAdminDisputes()
  const [open, setOpen] = useState<string | null>(null)
  const rows = q.data ?? []
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <section className={cn(panel, 'overflow-hidden')}>
        <p className={cn(h2, 'px-4 pt-3')}>Queue, oldest due first</p>
        {q.isLoading && <div className="p-4"><SkeletonRows /></div>}
        {q.error && <p className="p-4 text-[13px] text-token-danger">{errorText(q.error)}</p>}
        {!q.isLoading && rows.length === 0 && <EmptyState label="No disputes." />}
        <ul className="mt-2 divide-y divide-token-subtle">
          {rows.map((r) => <DisputeRow key={r.id} r={r} active={open === r.id} onOpen={() => setOpen(r.id)} />)}
        </ul>
      </section>
      <section className={cn(panel, 'min-w-0 p-4')}>
        {open ? <DisputeView id={open} /> : <p className="text-[13.5px] text-token-secondary">Pick a dispute to read both sides and decide.</p>}
      </section>
    </div>
  )
}

function DisputeRow({ r, active, onOpen }: { r: AdminDisputeRow; active: boolean; onOpen: () => void }) {
  const left = daysLeft(r.due_at)
  return (
    <li>
      <button type="button" onClick={onOpen} className={cn('w-full px-4 py-3 text-left', active ? 'bg-token-selected' : 'hover:bg-token-hover')}>
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-[14px] font-semibold text-token-primary">{r.title}</p>
          <span className="flex-none text-[13px] font-bold tabular-nums text-token-primary">{pesoFromCentavos(r.price_centavos)}</span>
        </div>
        <p className="text-[12.5px] text-token-secondary">{r.business} · {r.client ?? 'Client'} · {REASON_EN[r.reason]}</p>
        <p className="mt-0.5 text-[12px]">
          {r.status === 'decided'
            ? <span className="font-semibold text-token-muted">Decided: {DECISION_EN[r.decision ?? 'release']}</span>
            : <span className={cn('font-semibold', left.late ? 'text-token-danger' : 'text-token-warning')}>{r.status === 'deciding' ? 'Being decided · ' : ''}{left.text}</span>}
          <span className="text-token-muted"> · raised by the {r.raised_by_side}</span>
        </p>
      </button>
    </li>
  )
}

const DECISION_EN = { release: 'Released to the business', refund: 'Refunded to the client', split: 'Split' } as const
const METHOD_EN: Record<string, string> = { qrph: 'QR Ph', gcash: 'GCash', paymaya: 'Maya', card: 'card' }
const PAYMENT_EN: Record<string, string> = { paid: 'held', partially_refunded: 'part refunded', refunded: 'refunded' }

const LEDGER_LABEL: Record<string, string> = {
  charge: 'Client paid', fee: 'Payment partner fee', refund: 'Refund to client', commission: 'MySerbisyo commission',
  payout_fee: 'Payout fee', payout: 'Payout to business', platform_cover: 'Covered by MySerbisyo',
}

function DisputeView({ id }: { id: string }) {
  const q = useDisputeDetail(id)
  if (q.isLoading) return <SkeletonRows />
  if (q.error || !q.data) return <p className="text-[13px] text-token-danger">{q.error ? errorText(q.error) : 'Not found.'}</p>
  const d = q.data
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-[19px] font-bold leading-tight text-token-primary">{d.booking.title}</h2>
        <p className="text-[13px] text-token-secondary">{d.business.name} · {d.client.name ?? 'Client'} · booked for {fmtDateTime(d.booking.scheduled_start)}</p>
      </div>
      <div className="rounded-[12px] bg-token-danger-surface px-4 py-3">
        <p className="text-[13.5px] font-semibold text-token-primary">{REASON_EN[d.dispute.reason]}, raised by the {d.dispute.raised_by_side} on {fmtDateTime(d.dispute.created_at)}</p>
        <p className="mt-1 whitespace-pre-line text-[13.5px] text-token-primary">{d.dispute.description}</p>
        <p className="mt-1 text-[12.5px] text-token-secondary">Decide by {fmtDate(d.dispute.due_at, 'long')}.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Box title="Money">
          {d.payment && <p className="mb-2 text-[12.5px] text-token-secondary">Paid {fmtDateTime(d.payment.paid_at)} by {METHOD_EN[d.payment.method] ?? d.payment.method} · {PAYMENT_EN[d.payment.status] ?? d.payment.status}</p>}
          <table className="w-full text-[13px]">
            <tbody>
              {d.ledger.map((l) => (
                <tr key={l.id} className="border-t border-token-subtle first:border-0">
                  <td className="py-1 text-token-secondary">{LEDGER_LABEL[l.kind] ?? l.kind}</td>
                  <td className={cn('py-1 text-right tabular-nums', l.amount_centavos < 0 ? 'text-token-primary' : 'font-semibold text-token-success')}>{pesoFromCentavos(l.amount_centavos)}</td>
                </tr>
              ))}
              <tr className="border-t border-token-strong"><td className="py-1 font-semibold">Held now</td>
                <td className="py-1 text-right font-bold tabular-nums">{pesoFromCentavos(d.ledger.reduce((s, l) => s + l.amount_centavos, 0))}</td></tr>
            </tbody>
          </table>
        </Box>
        <Box title={`Conversation (${d.messages.length})`}>
          {d.messages.length === 0 && <p className="text-[13px] text-token-muted">No messages between them.</p>}
          <ol className="max-h-72 space-y-2 overflow-y-auto">
            {d.messages.map((m, i) => (
              <li key={i} className={cn('rounded-[10px] px-3 py-2 text-[13px]', m.side === 'client' ? 'bg-token-hover' : 'bg-token-selected')}>
                <p className="text-[11.5px] font-semibold text-token-muted">{m.side === 'client' ? 'Client' : 'Business'} · {fmtDateTime(m.at)}</p>
                {m.body && <p className="whitespace-pre-line text-token-primary">{m.body}</p>}
                {m.photo && <p className="flex items-center gap-1 text-token-secondary"><ImageIcon className="h-3.5 w-3.5" />Photo</p>}
              </li>
            ))}
          </ol>
        </Box>
      </div>
      {d.dispute.status === 'decided'
        ? <Box title="Decision"><p className="text-[13.5px] text-token-primary">{DECISION_EN[d.dispute.decision ?? 'release']}{d.dispute.refund_centavos ? `, ${pesoFromCentavos(d.dispute.refund_centavos)} back to the client` : ''} on {fmtDateTime(d.dispute.decided_at)}</p>
            {d.dispute.decision_note && <p className="text-[13px] text-token-secondary">{d.dispute.decision_note}</p>}</Box>
        : <DecideForm d={d} />}
    </div>
  )
}

function Box({ title, children }: { title: string; children: ReactNode }) {
  return <div className="rounded-[12px] border border-token-subtle p-3"><p className={cn(h2, 'mb-2')}>{title}</p>{children}</div>
}

const DECIDE_ERROR: Record<string, string> = {
  forbidden: 'Your session has no second factor. Sign in again.', not_open: 'Someone else is deciding this dispute, or it is already decided.',
  bad_split: 'The refund in a split must be more than ₱0 and less than the amount paid.', refund_failed: 'The payment partner refused the refund. Nothing changed.',
  finalize_failed: 'The refund was sent, but the decision was not saved. Decide again with the same amount to finish.',
  refund_already_sent: 'A refund was already sent for this dispute. Decide again with that amount.', own_business: 'You cannot decide a dispute about your own business.',
  note_required: 'Write a note for both sides.',
}

function DecideForm({ d }: { d: DisputeDetail }) {
  const { decide } = useOpsActions()
  const [confirm, confirmDialog] = useConfirm()
  const [decision, setDecision] = useState<'release' | 'refund' | 'split'>('release')
  const [refund, setRefund] = useState('')
  const [note, setNote] = useState('')
  const paid = d.payment?.amount_centavos ?? d.booking.price_centavos
  const refundCentavos = Math.round(Number(refund.replace(/[^\d.]/g, '')) * 100)
  const splitOk = decision !== 'split' || (refundCentavos > 0 && refundCentavos < paid)
  const ok = note.trim().length >= 5 && splitOk
  const summary = decision === 'release' ? `Release ${pesoFromCentavos(paid)} to ${d.business.name}, less the usual fees.`
    : decision === 'refund' ? `Refund ${pesoFromCentavos(paid)} to the client in full.`
    : `Refund ${pesoFromCentavos(refundCentavos || 0)} to the client and release the rest to ${d.business.name}.`

  const submit = async () => {
    const yes = await confirm({ title: 'Make this decision?', body: `${summary} This cannot be changed later.`, confirmLabel: 'Decide', tone: decision === 'release' ? undefined : 'danger' })
    if (!yes) return
    try {
      const r = await decide.mutateAsync({ disputeId: d.dispute.id, decision, refundCentavos: decision === 'split' ? refundCentavos : undefined, note: note.trim() })
      toast.success(`Decided. Payout ${pesoFromCentavos(r.result.payout ?? 0)}${r.refund ? `, refund ${pesoFromCentavos(r.refund)}` : ''}.`)
    } catch (e) {
      toast.error(DECIDE_ERROR[(e as Error).message] ?? errorText(e, 'The decision failed'))
    }
  }

  return (
    <Box title="Decide">
      {confirmDialog}
      <div className="space-y-3">
        <div role="radiogroup" aria-label="Decision" className="grid gap-2 sm:grid-cols-3">
          {([['release', 'Release to the business'], ['refund', 'Refund the client'], ['split', 'Split']] as const).map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={decision === k} onClick={() => setDecision(k)}
              className={cn('rounded-[10px] border px-3 py-2.5 text-[13.5px] font-semibold', decision === k ? 'border-brand bg-token-selected text-token-primary' : 'border-token-subtle text-token-secondary')}>{label}</button>
          ))}
        </div>
        {decision === 'split' && (
          <Field label={`Refund to the client, in pesos (paid ${pesoFromCentavos(paid)})`}>
            <Input inputMode="decimal" value={refund} onChange={(e) => setRefund(e.target.value)} placeholder="800" />
          </Field>
        )}
        <Field label="Note to both sides (they see it)">
          <Textarea rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <p className="text-[12.5px] text-token-secondary">{summary}</p>
        <div className="flex justify-end">
          <button type="button" className={btnPrimary} disabled={!ok || decide.isPending} onClick={submit}>
            {decide.isPending ? <><Loader2 className="h-4 w-4 animate-spin" />Deciding</> : <><Scale className="h-4 w-4" />Decide</>}
          </button>
        </div>
      </div>
    </Box>
  )
}

// --- Verification ----------------------------------------------------------------------------

const CHECK_KIND = { id: 'ID check', business: 'Business registration', licence: 'Licence' } as const

function Verifications() {
  const q = useAdminChecks()
  const [open, setOpen] = useState<string | null>(null)
  const rows = q.data ?? []
  const current = rows.find((r) => r.id === open) ?? null
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <section className={cn(panel, 'overflow-hidden')}>
        <p className={cn(h2, 'px-4 pt-3')}>Checks, oldest first</p>
        {q.isLoading && <div className="p-4"><SkeletonRows /></div>}
        {q.error && <p className="p-4 text-[13px] text-token-danger">{errorText(q.error)}</p>}
        {!q.isLoading && rows.length === 0 && <EmptyState label="No checks to make." />}
        <ul className="mt-2 divide-y divide-token-subtle">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => setOpen(r.id)} className={cn('w-full px-4 py-3 text-left', open === r.id ? 'bg-token-selected' : 'hover:bg-token-hover')}>
                <p className="text-[14px] font-semibold text-token-primary">{r.business}</p>
                <p className="text-[12.5px] text-token-secondary">{CHECK_KIND[r.kind]} · sent {fmtDateTime(r.submitted_at)}</p>
                <p className={cn('text-[12px] font-semibold', r.status === 'pending' ? 'text-token-warning' : r.status === 'approved' ? 'text-token-success' : 'text-token-danger')}>
                  {r.status === 'pending' ? 'To check' : r.status === 'approved' ? 'Approved' : 'Not approved'}</p>
              </button>
            </li>
          ))}
        </ul>
      </section>
      <section className={cn(panel, 'min-w-0 p-4')}>
        {current ? <CheckView c={current} key={current.id} /> : <p className="text-[13.5px] text-token-secondary">Pick a check to see the photos and decide.</p>}
      </section>
    </div>
  )
}

function CheckView({ c }: { c: AdminCheck }) {
  const photos = useCheckPhotos(c.status === 'pending' ? c : null)
  const decide = useDecideCheck()
  const [confirm, confirmDialog] = useConfirm()
  const [reason, setReason] = useState('')
  const details: Array<[string, string | null]> = c.kind === 'id'
    ? [['ID', `${idTypeLabel(c.id_type)}, number ending ${c.number_last4}`]]
    : c.kind === 'business'
      ? [['Registration', `${c.reg_kind?.toUpperCase()} ${c.reg_number}`], ['Registered name', c.business_name], ['TIN ends', c.number_last4]]
      : [['Licence', `${c.licence_trade ?? ''} · ${c.licence_body} ${c.licence_number}`], ['Valid until', c.licence_expires ? fmtDate(c.licence_expires + 'T12:00:00+08:00', 'long') : null]]
  const run = async (approve: boolean) => {
    const ok = await confirm(approve
      ? { title: `Approve this ${CHECK_KIND[c.kind].toLowerCase()}?`, body: c.kind === 'id' ? 'The business becomes findable, bookable and payable. The photos are then deleted.' : 'The badge shows on the profile. The photo is then deleted.', confirmLabel: 'Approve' }
      : { title: 'Not approve?', body: `The owner sees: "${reason.trim()}". The photos are then deleted, and the owner can send again.`, confirmLabel: 'Not approve', tone: 'danger' })
    if (!ok) return
    try {
      const r = await decide.mutateAsync({ checkId: c.id, approve, reason: approve ? undefined : reason.trim() })
      toast.success(`${approve ? 'Approved' : 'Not approved'}. ${r.photosDeleted ? 'Photos deleted.' : 'The photos could not be deleted. Decide again later to retry.'}`)
    } catch (e) { toast.error(errorText(e, 'The decision failed')) }
  }
  return (
    <div className="space-y-4">
      {confirmDialog}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-display text-[19px] font-bold leading-tight text-token-primary">{c.business}</h2>
          <p className="text-[13px] text-token-secondary">{CHECK_KIND[c.kind]} · sent by {c.owner?.name || 'the owner'}{c.owner?.mobile ? ` · +${c.owner.mobile}` : ''} on {fmtDateTime(c.submitted_at)}</p>
        </div>
        {c.slug && <a href={`/p/${c.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand hover:underline">Listing<ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13.5px]">
        {details.filter(([, v]) => v).map(([k, v]) => <Fragment key={k}><dt className="text-token-secondary">{k}</dt><dd className="text-token-primary">{v}</dd></Fragment>)}
        {c.kind === 'id' && <><dt className="text-token-secondary">Vendor</dt><dd className="text-token-primary">{c.provider === 'manual' ? 'None yet. Compare the face and the details yourself.' : `${c.provider}: ${c.vendor_verdict}`}</dd></>}
      </dl>
      {c.status === 'pending' ? (
        <>
          {photos.isLoading && <SkeletonRows count={2} />}
          {photos.error && <p className="text-[13px] text-token-danger">{errorText(photos.error)}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            {(photos.data ?? []).map((p) => (
              <figure key={p.path} className="overflow-hidden rounded-[12px] border border-token-subtle">
                <img src={p.url} alt="" className="max-h-80 w-full bg-token-hover object-contain" />
                <figcaption className="px-3 py-1.5 text-[12px] font-semibold text-token-secondary">{/selfie/.test(p.path) ? 'Selfie' : /certificate/.test(p.path) ? 'Certificate' : /licence/.test(p.path) ? 'Licence' : 'ID'}</figcaption>
              </figure>
            ))}
          </div>
          <Field label="Reason, if you do not approve (the owner sees it)">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="The photo is blurred. Take it again in good light." />
          </Field>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={cn(btnSecondary, 'text-token-danger')} disabled={reason.trim().length < 5 || decide.isPending} onClick={() => run(false)}>Not approve</button>
            <button type="button" className={btnPrimary} disabled={decide.isPending} onClick={() => run(true)}>{decide.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Approve</button>
          </div>
        </>
      ) : (
        <p className="text-[13.5px] text-token-secondary">{c.status === 'approved' ? 'Approved' : 'Not approved'} on {fmtDateTime(c.decided_at)}.{c.status === 'rejected' ? ` Reason: ${c.reason}` : ''} {c.files.length ? 'The photos are still stored. Delete them from the storage console.' : 'The photos were deleted.'}</p>
      )}
    </div>
  )
}

// --- Payouts ---------------------------------------------------------------------------------

function Payouts() {
  const q = usePayoutQueue()
  const { markSent } = useOpsActions()
  const [refs, setRefs] = useState<Record<string, string>>({})
  const rows = q.data ?? []
  return (
    <section className={cn(panel, 'p-4')}>
      <p className={h2}>Payouts to send by hand</p>
      <p className="mt-1 text-[12.5px] text-token-secondary">Send each one from the MySerbisyo account, then record the transfer reference. The business then sees "sent".</p>
      {q.isLoading && <div className="mt-3"><SkeletonRows /></div>}
      {!q.isLoading && rows.length === 0 && <EmptyState label="Nothing to send." />}
      <ul className="mt-3 space-y-3">
        {rows.map((p) => (
          <li key={p.id} className="rounded-[12px] border border-token-subtle p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-token-primary">{p.business}</p>
                <p className="text-[12.5px] text-token-secondary">{p.booking_title} · released {fmtDateTime(p.created_at)}</p>
              </div>
              <span className="font-display text-[18px] font-bold tabular-nums text-token-primary">{pesoFromCentavos(p.amount_centavos)}</span>
            </div>
            {p.account
              ? <p className="mt-1 flex items-center gap-2 text-[13px] text-token-primary"><Wallet className="h-4 w-4 text-token-muted" />{p.account.kind === 'bank' ? p.account.bank : p.account.kind === 'gcash' ? 'GCash' : 'Maya'} · {p.account.name} · <span className="font-mono">{p.account.number}</span></p>
              : <p className="mt-1 text-[13px] font-semibold text-token-warning">No payout account yet. The business has been asked to add one.</p>}
            {p.status === 'queued' && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Input className="max-w-xs" placeholder="Transfer reference" value={refs[p.id] ?? ''} onChange={(e) => setRefs({ ...refs, [p.id]: e.target.value })} />
                <button type="button" className={btnPrimary} disabled={!(refs[p.id] ?? '').trim() || markSent.isPending} onClick={async () => {
                  try { await markSent.mutateAsync({ payout: p.id, reference: refs[p.id] }); toast.success('Recorded as sent') } catch (e) { toast.error(errorText(e)) }
                }}>Mark sent</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

// --- Holidays --------------------------------------------------------------------------------

function Holidays() {
  const q = useHolidays()
  const { setHoliday } = useOpsActions()
  const [confirm, confirmDialog] = useConfirm()
  const [day, setDay] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'regular' | 'special'>('regular')
  const thisYear = new Date().getFullYear()
  const rows = (q.data ?? []).filter((h) => Number(h.day.slice(0, 4)) >= thisYear)
  return (
    <section className={cn(panel, 'space-y-4 p-4')}>
      {confirmDialog}
      <div>
        <p className={h2}>Holidays</p>
        <p className="mt-1 text-[12.5px] text-token-secondary">The automatic release does not count these days. Add a holiday as soon as it is proclaimed.</p>
      </div>
      <form className="flex flex-wrap items-end gap-2" onSubmit={async (e) => {
        e.preventDefault()
        try { await setHoliday.mutateAsync({ day, name, kind }); setDay(''); setName(''); toast.success('Holiday saved') } catch (err) { toast.error(errorText(err)) }
      }}>
        <Field label="Date"><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} /></Field>
        <Field label="Name" className="min-w-48 flex-1"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Eid'l Fitr" /></Field>
        <Field label="Kind"><Select value={kind} onChange={(e) => setKind(e.target.value as 'regular' | 'special')}><option value="regular">Regular</option><option value="special">Special non-working</option></Select></Field>
        <button type="submit" className={btnPrimary} disabled={!day || !name.trim() || setHoliday.isPending}>Add</button>
      </form>
      {q.isLoading ? <SkeletonRows /> : (
        <ul className="divide-y divide-token-subtle">
          {rows.map((h) => (
            <li key={h.day} className="flex items-center justify-between gap-2 py-2">
              <span className="flex items-center gap-2 text-[13.5px] text-token-primary"><CalendarDays className="h-4 w-4 text-token-muted" />
                <span className="w-32 tabular-nums">{fmtDate(h.day + 'T12:00:00+08:00', 'medium')}</span>{h.name}
                <span className="text-[12px] text-token-muted">{h.kind}</span></span>
              <button type="button" className="text-[12.5px] font-semibold text-token-danger hover:underline" onClick={async () => {
                if (await confirm({ title: `Remove ${h.name}?`, body: 'The automatic release will count this day again.', confirmLabel: 'Remove', tone: 'danger' })) {
                  try { await setHoliday.mutateAsync({ ...h, remove: true }) } catch (e) { toast.error(errorText(e)) }
                }
              }}>Remove</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// --- Accounts --------------------------------------------------------------------------------

function Accounts() {
  const [q, setQ] = useState('')
  const r = useFindAccounts(q)
  return (
    <section className={cn(panel, 'space-y-3 p-4')}>
      <div>
        <p className={h2}>Accounts</p>
        <p className="mt-1 text-[12.5px] text-token-secondary">A suspended client cannot post, offer, book or message. A suspended business is also hidden from search and its page.</p>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, business or mobile (3 characters or more)" />
      {r.isFetching && <SkeletonRows count={2} />}
      {r.data && r.data.length === 0 && <EmptyState label="No account matches." />}
      <ul className="space-y-2">{(r.data ?? []).map((a) => <AccountItem key={a.kind + a.id} a={a} />)}</ul>
    </section>
  )
}

function AccountItem({ a }: { a: AccountRow }) {
  const { suspend } = useOpsActions()
  const [reason, setReason] = useState('')
  const run = async (lift: boolean) => {
    try { await suspend.mutateAsync({ kind: a.kind, id: a.id, reason, lift }); toast.success(lift ? 'Suspension lifted' : 'Suspended'); setReason('') } catch (e) { toast.error(errorText(e)) }
  }
  return (
    <li className="rounded-[12px] border border-token-subtle p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[14px] font-semibold text-token-primary">{a.name ?? 'No name'} <span className="text-[12px] font-normal text-token-muted">{a.kind === 'company' ? 'business' : 'person'}{a.mobile ? ` · +${a.mobile.replace(/^\+/, '')}` : ''}</span></p>
        {a.suspended_at && <span className="rounded-full bg-token-danger-surface px-2.5 py-0.5 text-[12px] font-semibold text-token-danger">Suspended {fmtDate(a.suspended_at)}</span>}
      </div>
      {a.suspended_at
        ? <div className="mt-2 flex flex-wrap items-center justify-between gap-2"><p className="text-[13px] text-token-secondary">{a.suspended_reason}</p>
            <button type="button" className={btnSecondary} disabled={suspend.isPending} onClick={() => run(true)}>Lift suspension</button></div>
        : <div className="mt-2 flex flex-wrap items-center gap-2"><Input className="max-w-sm" placeholder="Reason (the log keeps it)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button type="button" className={cn(btnSecondary, 'text-token-danger')} disabled={!reason.trim() || suspend.isPending} onClick={() => run(false)}><UserX className="h-4 w-4" />Suspend</button></div>}
    </li>
  )
}
